import { useState } from "react";
import { toast } from "sonner";
import { Download, FileCheck2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApi, errMsg } from "@/lib/api";
import { PageHead, card, Empty, inputCls, Sel } from "@/components/admin/kit";
import { AuditList, MonthlyReport, SampleReport, download, thisMonth, useFetch } from "@/components/p4/shared";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const NEXT = { draft: ["sent", "Mark sent"], sent: ["paid", "Mark paid"] };

function Usage({ api, month }) {
  const [u] = useFetch(api, `/admin/usage?month=${month}`);
  if (!u) return <Empty>Loading…</Empty>;
  const cols = [["Calls", "calls"], ["Answered", "answered"], ["Minutes", "minutes"], ["Twilio cost", "provider_cost", inr], ["Sarvam est.", "sarvam_cost", inr],
    ["Active enrol.", "active_enrollments"], ["Billable", "billable"], ["Price", "price", inr], ["Revenue", "revenue", inr], ["Margin", "margin", inr]];
  return (
    <div className={`${card} overflow-x-auto`} data-testid="usage-table">
      <p className="mb-2 text-xs text-muted-foreground">Real calls only. Merged calls split equally between the enrollments covered. Twilio cost in ₹ at {u.usd_inr} per USD; Sarvam is an estimate at ₹{u.sarvam_inr_per_10k_chars} per 10,000 characters. Sample and REAL TEST ONLY patients are never billable.</p>
      <table className="w-full text-sm"><thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1">Organisation</th>{cols.map(([l]) => <th key={l} className="px-1">{l}</th>)}</tr></thead>
        <tbody>{u.orgs.map((x) => (
          <tr key={x.org_id} className="border-t" data-testid={`usage-${x.org_id}`}><td className="py-1 font-semibold">{x.org}{x.trial && <span className="ml-1 rounded-full bg-amber-100 px-2 text-[11px]">trial{x.trial_end ? ` to ${x.trial_end}` : ""} · billed {x.paid_days}/{x.days_in_month} days · full month {inr(x.would_be)}</span>}</td>
            {cols.map(([l, k, f]) => <td key={l} className="px-1">{f ? f(x[k]) : x[k]}</td>)}</tr>))}</tbody></table>
    </div>
  );
}

function Invoices({ api, month }) {
  const [list, reload] = useFetch(api, `/admin/invoices?month=${month}`);
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className={card} data-testid="invoices">
      <div className="mb-2 flex items-center gap-2"><p className="mr-auto font-bold">Invoices · {month}</p>
        <Button size="sm" onClick={() => act(() => api.post("/admin/invoices/generate", { month }), "Draft invoices ready to review")} data-testid="invoices-generate-btn"><FileCheck2 size={14} /> Make / refresh drafts</Button></div>
      {!list ? <Empty>Loading…</Empty> : !list.length ? <Empty>No invoices for this month yet.</Empty> : list.map((i) => (
        <div key={i.id} className="flex flex-wrap items-center gap-2 border-t py-2 text-sm" data-testid={`invoice-${i.number}`}>
          <b className="w-44">{i.number}</b><span className="flex-1">{i.org} · {i.billable} × {inr(i.price)} = <b data-testid="invoice-amount">{inr(i.amount)}</b>{i.trial && <span className="text-muted-foreground"> (trial — billed {i.paid_days}/{i.days_in_month} days, full month {inr(i.would_be)})</span>}</span>
          <span className="rounded-full bg-[var(--lav)] px-2 text-xs font-semibold" data-testid="invoice-status">{i.status}</span>
          {NEXT[i.status] && <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/admin/invoices/${i.id}/status`, { status: NEXT[i.status][0] }), NEXT[i.status][1])} data-testid={`invoice-next-${i.number}`}>{NEXT[i.status][1]}</Button>}
          <Button size="sm" variant="ghost" onClick={() => download(api, `/admin/invoices/${i.id}/pdf`, `${i.number}.pdf`)} data-testid={`invoice-pdf-${i.number}`}><Download size={14} /> PDF</Button>
        </div>))}
    </div>
  );
}

export default function Billing() {
  const api = useApi();
  const [month, setMonth] = useState(thisMonth());
  return (
    <div className="grid gap-4" data-testid="billing-page">
      <PageHead title="Usage, cost & billing" sub="Super admin only. Organisations never see cost or minutes.">
        <input type="month" className={`${inputCls} w-40`} value={month} onChange={(e) => setMonth(e.target.value)} data-testid="billing-month" />
      </PageHead>
      <Usage api={api} month={month} />
      <Invoices api={api} month={month} />
    </div>
  );
}

export function OrgReports() {
  const api = useApi();
  const [orgs] = useFetch(api, "/hospitals");
  const [hid, setHid] = useState("");
  const [sample, setSample] = useState(false);
  return (
    <div className="grid gap-4" data-testid="org-reports-page">
      <PageHead title="Monthly reports" sub="Organisation and per-doctor reports, by month. Real calls only.">
        <Button variant={sample ? "default" : "outline"} onClick={() => setSample(!sample)} data-testid="sample-report-btn">{sample ? "Hide sample report" : "Sample report"}</Button>
        <Sel value={hid} onChange={setHid} options={(orgs || []).map((h) => [h.id, h.name])} placeholder="Choose organisation" testid="report-org-select" />
      </PageHead>
      {sample && <SampleReport api={api} />}
      {hid ? <MonthlyReport api={api} path={`/admin/reports4/${hid}`} title={(orgs || []).find((h) => h.id === hid)?.name} /> : <Empty>Choose an organisation.</Empty>}
    </div>
  );
}

export function AdminAudit() {
  const api = useApi();
  return <div className="grid gap-4"><PageHead title="Audit log" sub="Every create, edit, approve, send back, pause, stop, restart, delete, view-as, price change and invoice change." /><AuditList api={api} path="/admin/audit" /></div>;
}
